<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeMount, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { getRandomColor } from 'dashboard/helper/labelColor';
import {
  buildConversationStageDescription,
  CONVERSATION_STAGE_LABELS,
  EMPTY_CONVERSATION_STAGE_ID,
  formatConversationStageLabel,
  getConversationStageOrder,
  isConversationStageLabel,
  LEGACY_CONVERSATION_STAGE_LABEL_MAP,
  normalizeConversationStageLabel,
} from 'dashboard/constants/conversationStages';
import ConversationApi from 'dashboard/api/inbox/conversation';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();

const loading = ref({});
const showStagePopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedStage = ref({});
const title = ref('');
const color = ref(getRandomColor());
const draggedStageTitle = ref('');
const stageCounts = ref({});
const isSavingStage = ref(false);

const accountLabels = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const savedStageLabels = computed(() =>
  accountLabels.value.filter(isConversationStageLabel)
);

const stages = computed(() => {
  const defaultStages = savedStageLabels.value.length
    ? []
    : CONVERSATION_STAGE_LABELS.map((stage, index) => {
        const savedLabel = accountLabels.value.find(
          label => normalizeConversationStageLabel(label.title) === stage
        );
        return [
          stage,
          {
            id: savedLabel?.id,
            title: savedLabel?.title || stage,
            displayTitle: formatConversationStageLabel(stage),
            color: savedLabel?.color || '#1f93ff',
            description:
              savedLabel?.description ||
              buildConversationStageDescription(index),
            order: savedLabel ? getConversationStageOrder(savedLabel) : index,
            isSaved: !!savedLabel,
          },
        ];
      });
  const stageMap = new Map(defaultStages);

  savedStageLabels.value.forEach(label => {
    const normalizedTitle = normalizeConversationStageLabel(label.title);
    stageMap.set(normalizedTitle, {
      ...label,
      title: normalizedTitle,
      displayTitle: formatConversationStageLabel(normalizedTitle),
      order: getConversationStageOrder(label),
      isSaved: true,
    });
  });

  return [...stageMap.values()].sort(
    (a, b) => a.order - b.order || a.title.localeCompare(b.title)
  );
});

const emptyStageCount = computed(
  () => Number(stageCounts.value[EMPTY_CONVERSATION_STAGE_ID]) || 0
);

const stageCountFor = stage =>
  Number(stageCounts.value[normalizeConversationStageLabel(stage.title)]) || 0;

const refreshStageCounts = async () => {
  try {
    const {
      data: { payload },
    } = await ConversationApi.counts({ status: 'all', assigneeType: 'all' });
    const nextCounts = {};

    Object.entries(payload?.labels || {}).forEach(([label, count]) => {
      if (label === EMPTY_CONVERSATION_STAGE_ID) {
        nextCounts[EMPTY_CONVERSATION_STAGE_ID] = Number(count) || 0;
        return;
      }

      const normalizedLabel = normalizeConversationStageLabel(label);
      const stage =
        LEGACY_CONVERSATION_STAGE_LABEL_MAP[label] ||
        LEGACY_CONVERSATION_STAGE_LABEL_MAP[normalizedLabel] ||
        normalizedLabel;

      nextCounts[stage] = (nextCounts[stage] || 0) + Number(count || 0);
    });

    stageCounts.value = nextCounts;
  } catch {
    stageCounts.value = {};
  }
};

const refreshStages = async () => {
  await store.dispatch('labels/get');
  await refreshStageCounts();
};

const openAddPopup = () => {
  selectedStage.value = {};
  title.value = '';
  color.value = getRandomColor();
  showStagePopup.value = true;
};

const openEditPopup = stage => {
  selectedStage.value = stage;
  title.value = stage.displayTitle;
  color.value = stage.color;
  showStagePopup.value = true;
};

const hideStagePopup = () => {
  showStagePopup.value = false;
};

const openDeletePopup = stage => {
  selectedStage.value = stage;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const saveStage = async () => {
  const normalizedTitle = normalizeConversationStageLabel(title.value);
  if (!normalizedTitle) return;

  const order = selectedStage.value.order ?? stages.value.length;
  const payload = {
    color: color.value,
    description: buildConversationStageDescription(order),
    title: normalizedTitle,
    show_on_sidebar: false,
  };

  isSavingStage.value = true;
  try {
    if (selectedStage.value.id) {
      await store.dispatch('labels/update', {
        id: selectedStage.value.id,
        ...payload,
      });
      useAlert('Etapa atualizada com sucesso');
    } else {
      await store.dispatch('labels/create', payload);
      useAlert('Etapa criada com sucesso');
    }
    await refreshStages();
    hideStagePopup();
  } catch (error) {
    useAlert(error.message || 'Não foi possível salvar a etapa');
  } finally {
    isSavingStage.value = false;
  }
};

const deleteStage = async () => {
  const { id, title: stageTitle } = selectedStage.value;

  if (!id) {
    useAlert('Edite a etapa antes de remover');
    closeDeletePopup();
    return;
  }

  loading.value[id] = true;
  try {
    await store.dispatch('labels/delete', id);
    await refreshStages();
    useAlert(
      `Etapa ${formatConversationStageLabel(stageTitle)} removida. Clientes ficaram em Vazio.`
    );
  } catch (error) {
    useAlert(error.message || 'Não foi possível remover a etapa');
  } finally {
    loading.value[id] = false;
    closeDeletePopup();
  }
};

const ensureSavedStage = async (stage, order) => {
  const payload = {
    color: stage.color,
    description: buildConversationStageDescription(order),
    title: stage.title,
    show_on_sidebar: false,
  };

  if (stage.id) {
    await store.dispatch('labels/update', { id: stage.id, ...payload });
  } else {
    await store.dispatch('labels/create', payload);
  }
};

const moveStage = async (fromTitle, toTitle) => {
  if (!fromTitle || fromTitle === toTitle) return;

  const orderedStages = [...stages.value];
  const fromIndex = orderedStages.findIndex(stage => stage.title === fromTitle);
  const toIndex = orderedStages.findIndex(stage => stage.title === toTitle);
  if (fromIndex < 0 || toIndex < 0) return;

  const [stage] = orderedStages.splice(fromIndex, 1);
  orderedStages.splice(toIndex, 0, stage);

  try {
    await Promise.all(
      orderedStages.map((item, index) => ensureSavedStage(item, index))
    );
    await refreshStages();
    useAlert('Ordem das etapas atualizada');
  } catch (error) {
    useAlert(error.message || 'Não foi possível ordenar as etapas');
  } finally {
    draggedStageTitle.value = '';
  }
};

const moveStageByOffset = async (stage, offset) => {
  const orderedStages = [...stages.value];
  const fromIndex = orderedStages.findIndex(item => item.title === stage.title);
  const toIndex = fromIndex + offset;
  if (fromIndex < 0 || toIndex < 0 || toIndex >= orderedStages.length) return;

  await moveStage(stage.title, orderedStages[toIndex].title);
};

onBeforeMount(async () => {
  await refreshStages();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    loading-message="Carregando etapas"
    :no-records-found="false"
    no-records-message="Nenhuma etapa encontrada"
  >
    <template #header>
      <BaseSettingsHeader
        title="Configurar etapas"
        description="Crie, edite e ordene as etapas usadas nos filtros, cards, botão Etapa e Kanban."
        search-placeholder="Buscar etapas"
        feature-name="conversation-stages"
      >
        <template #count>
          <span class="text-body-main text-n-slate-11">
            {{ stages.length }} etapas
          </span>
        </template>
        <template #actions>
          <Button label="Nova etapa" size="sm" @click="openAddPopup" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="grid gap-2 max-w-3xl">
        <p class="m-0 mb-2 text-xs text-n-slate-10">
          Para alterar a ordem, arraste pelo ícone de pontos ou use as setas.
        </p>
        <div
          class="flex items-center gap-3 px-3 py-2 border border-dashed rounded-lg bg-n-alpha-1 border-n-weak"
        >
          <i class="text-base i-lucide-circle-off text-n-slate-9" />
          <span
            class="w-4 h-4 border border-solid rounded border-n-weak bg-n-slate-6"
          />
          <div class="min-w-0 flex-1">
            <p class="m-0 text-sm font-medium truncate text-n-slate-12">
              Vazio
            </p>
            <p class="m-0 text-xs text-n-slate-10">
              Clientes sem etapa definida
            </p>
          </div>
          <span
            class="px-2 py-1 text-xs font-medium rounded-md text-n-slate-11 bg-n-alpha-2"
          >
            {{ emptyStageCount }} clientes
          </span>
        </div>
        <div
          v-for="(stage, index) in stages"
          :key="stage.title"
          draggable="true"
          class="flex items-center gap-3 px-3 py-2 border border-solid rounded-lg cursor-grab bg-n-alpha-2 border-n-weak active:cursor-grabbing"
          @dragstart="draggedStageTitle = stage.title"
          @dragover.prevent
          @drop="moveStage(draggedStageTitle, stage.title)"
        >
          <i class="text-base i-lucide-grip-vertical text-n-slate-9" />
          <span
            class="w-4 h-4 border border-solid rounded border-n-weak"
            :style="{ backgroundColor: stage.color }"
          />
          <div class="min-w-0 flex-1">
            <p class="m-0 text-sm font-medium truncate text-n-slate-12">
              {{ stage.displayTitle }}
            </p>
            <p class="m-0 text-xs text-n-slate-10">
              Ordem {{ stage.order + 1 }}
            </p>
          </div>
          <span
            class="px-2 py-1 text-xs font-medium rounded-md text-n-slate-11 bg-n-alpha-2"
          >
            {{ stageCountFor(stage) }} clientes
          </span>
          <div class="flex items-center gap-1">
            <Button
              v-tooltip.top="'Subir'"
              icon="i-lucide-arrow-up"
              slate
              xs
              faded
              :disabled="index === 0"
              @click="moveStageByOffset(stage, -1)"
            />
            <Button
              v-tooltip.top="'Descer'"
              icon="i-lucide-arrow-down"
              slate
              xs
              faded
              :disabled="index === stages.length - 1"
              @click="moveStageByOffset(stage, 1)"
            />
          </div>
          <Button
            v-tooltip.top="'Editar'"
            icon="i-lucide-pencil"
            slate
            xs
            faded
            @click="openEditPopup(stage)"
          />
          <Button
            v-tooltip.top="'Excluir'"
            icon="i-woot-bin"
            ruby
            xs
            faded
            :is-loading="loading[stage.id]"
            @click="openDeletePopup(stage)"
          />
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showStagePopup" :on-close="hideStagePopup">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          :header-title="
            selectedStage.id || selectedStage.title
              ? 'Editar etapa'
              : 'Nova etapa'
          "
          header-content="Essa etapa aparecerá nos filtros, no botão Etapa, nos cards e no Kanban."
        />
        <form class="flex flex-wrap mx-0" @submit.prevent="saveStage">
          <woot-input
            v-model="title"
            class="w-full label-name--input"
            label="Nome da etapa"
            placeholder="Ex: aguardando pagamento"
          />
          <div class="w-full">
            <label>
              Cor
              <woot-color-picker v-model="color" />
            </label>
          </div>
          <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
            <Button
              faded
              slate
              type="reset"
              label="Cancelar"
              @click.prevent="hideStagePopup"
            />
            <Button
              type="submit"
              label="Salvar"
              :disabled="!title.trim() || isSavingStage"
              :is-loading="isSavingStage"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="deleteStage"
      title="Remover etapa"
      message="Tem certeza que deseja remover"
      :message-value="` ${selectedStage.displayTitle}? Os clientes desta etapa ficarão em Vazio.`"
      confirm-text="Sim, remover"
      reject-text="Cancelar"
    />
  </SettingsLayout>
</template>
